import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import DocumentPreviewModal from "../components/DocumentPreviewModal";
import InvestorPageHeader from "../components/investor/InvestorPageHeader";
import { fetchMyDeposits, getMyDepositScreenshotSignedUrl } from "../services/paymentService";

function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (data?.results && Array.isArray(data.results)) return data.results;
  return [];
}

export default function WalletDepositRequestsPage() {
  const location = useLocation();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [docPreview, setDocPreview] = useState({
    open: false,
    loading: false,
    url: "",
    error: "",
    title: "Deposit proof",
    subtitle: "",
    authRequired: false,
    contentHint: "",
  });
  const flash = location.state?.message;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await fetchMyDeposits();
        if (!cancelled) setDeposits(unwrapList(data));
      } catch {
        if (!cancelled) {
          setDeposits([]);
          setError("Could not load deposit requests.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const closeDocPreview = () => {
    setDocPreview({
      open: false,
      loading: false,
      url: "",
      error: "",
      title: "Deposit proof",
      subtitle: "",
      authRequired: false,
      contentHint: "",
    });
  };

  const onViewDoc = async (deposit) => {
    const depositId = deposit?.id;
    if (!depositId) return;
    setError("");
    setDocPreview({
      open: true,
      loading: true,
      url: "",
      error: "",
      title: "Deposit proof",
      subtitle: [deposit.bank_channel, deposit.transaction_ref, deposit.amount != null ? `BDT ${deposit.amount}` : ""]
        .filter(Boolean)
        .join(" · "),
      authRequired: false,
      contentHint: "",
    });
    try {
      const { data } = await getMyDepositScreenshotSignedUrl(depositId);
      if (!data?.url) throw new Error("No proof URL returned.");
      setDocPreview((prev) => ({
        ...prev,
        loading: false,
        url: data.url,
        error: "",
        authRequired: Boolean(data.auth_required),
        contentHint: data.content_hint || "",
      }));
    } catch (err) {
      const d = err.response?.data;
      const msg = d?.detail || "Could not open deposit proof.";
      setDocPreview((prev) => ({ ...prev, loading: false, url: "", error: msg }));
      setError(msg);
    }
  };

  return (
    <div className="page investor-premium investor-panel wallet-page">
      <p className="wallet-breadcrumb">
        <Link to="/wallet">← Wallet & overview</Link>
      </p>
      <InvestorPageHeader
        icon="deposit"
        title="My deposit requests"
        subtitle="Status of deposits you have submitted (newest first)."
      />

      <div className="modal-tab-row" role="tablist" aria-label="Deposit views" style={{ marginBottom: 16 }}>
        <Link
          to="/wallet?deposit=new"
          role="tab"
          className="modal-tab"
          style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
        >
          New request
        </Link>
        <span role="tab" aria-selected="true" className="modal-tab modal-tab--active">
          My deposit requests
        </span>
      </div>

      {flash ? (
        <p className="msg msg-success" style={{ margin: "0 0 12px" }}>
          {flash}
        </p>
      ) : null}
      {error ? (
        <p className="msg msg-error" style={{ margin: "0 0 12px" }}>
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="muted" style={{ margin: 0 }}>
          Loading…
        </p>
      ) : deposits.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          No deposit requests yet.
        </p>
      ) : (
        <div className="table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>SL</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Channel</th>
                <th>Reference</th>
                <th>Status</th>
                <th>Doc</th>
              </tr>
            </thead>
            <tbody>
              {deposits.slice(0, 60).map((d, index) => (
                <tr key={d.id}>
                  <td>{index + 1}</td>
                  <td>{d.transaction_date}</td>
                  <td>BDT {d.amount}</td>
                  <td>{d.bank_channel}</td>
                  <td style={{ fontSize: 13 }}>{d.transaction_ref}</td>
                  <td>
                    <span className={`badge badge-${d.status}`}>{d.status}</span>
                    {d.status === "REJECTED" && d.rejection_reason && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {d.rejection_reason}
                      </div>
                    )}
                    {d.status === "APPROVED" && (d.admin_remark || "").trim() ? (
                      <div className="deposit-admin-note" style={{ marginTop: 8 }}>
                        <span className="deposit-admin-note__label">Admin note</span>
                        <div style={{ whiteSpace: "pre-wrap" }}>{(d.admin_remark || "").trim()}</div>
                        {d.reviewed_at ? (
                          <div className="deposit-admin-note__meta">
                            Approved {new Date(d.reviewed_at).toLocaleString()}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {d.status === "APPROVED" && !(d.admin_remark || "").trim() && d.reviewed_at ? (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        Approved {new Date(d.reviewed_at).toLocaleString()}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {d.screenshot_url ? (
                      <button type="button" className="btn-secondary compact-btn" onClick={() => onViewDoc(d)}>
                        Doc View
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DocumentPreviewModal
        open={docPreview.open}
        url={docPreview.url}
        title={docPreview.title}
        subtitle={docPreview.subtitle}
        loading={docPreview.loading}
        error={docPreview.error}
        authRequired={docPreview.authRequired}
        contentHint={docPreview.contentHint}
        onClose={closeDocPreview}
      />
    </div>
  );
}
